import { useState, useEffect, useCallback } from "react";
import LocalStorageManager from "@/lib/localStorage";

/**
 * Generic hook for localStorage with automatic sync
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  // Initialize state with value from localStorage
  const [storedValue, setStoredValue] = useState<T>(() => {
    return LocalStorageManager.getItem(key, defaultValue);
  });

  // Update localStorage when state changes
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        // Allow value to be a function so we have the same API as useState
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;

        setStoredValue(valueToStore);
        LocalStorageManager.setItem(key, valueToStore);
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue],
  );

  // Remove item from localStorage
  const removeValue = useCallback(() => {
    try {
      setStoredValue(defaultValue);
      LocalStorageManager.removeItem(key);
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, defaultValue]);

  // Listen for changes in other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue));
        } catch (error) {
          console.warn(
            `Error parsing localStorage change for key "${key}":`,
            error,
          );
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [key]);

  return [storedValue, setValue, removeValue];
}

/**
 * Hook for managing arrays in localStorage with helper methods
 */
export function useLocalStorageArray<T>(
  key: string,
  defaultValue: T[] = [],
): {
  items: T[];
  addItem: (item: T) => void;
  removeItem: (predicate: (item: T) => boolean) => void;
  updateItem: (
    predicate: (item: T) => boolean,
    updater: (item: T) => T,
  ) => void;
  clearItems: () => void;
  setItems: (items: T[]) => void;
} {
  const [items, setItems] = useLocalStorage<T[]>(key, defaultValue);

  const addItem = useCallback(
    (item: T) => {
      setItems((prev) => [...prev, item]);
    },
    [setItems],
  );

  const removeItem = useCallback(
    (predicate: (item: T) => boolean) => {
      setItems((prev) => prev.filter((item) => !predicate(item)));
    },
    [setItems],
  );

  const updateItem = useCallback(
    (predicate: (item: T) => boolean, updater: (item: T) => T) => {
      setItems((prev) =>
        prev.map((item) => (predicate(item) ? updater(item) : item)),
      );
    },
    [setItems],
  );

  const clearItems = useCallback(() => {
    setItems([]);
  }, [setItems]);

  return {
    items,
    addItem,
    removeItem,
    updateItem,
    clearItems,
    setItems,
  };
}

/**
 * Hook for managing objects in localStorage with deep merge
 */
export function useLocalStorageObject<T extends Record<string, unknown>>(
  key: string,
  defaultValue: T,
): [T, (updates: Partial<T>) => void, () => void] {
  const [value, setValue, removeValue] = useLocalStorage(key, defaultValue);

  const updateValue = useCallback(
    (updates: Partial<T>) => {
      setValue((prev) => ({ ...prev, ...updates }));
    },
    [setValue],
  );

  return [value, updateValue, removeValue];
}

/**
 * Hook for managing boolean flags in localStorage
 */
export function useLocalStorageBoolean(
  key: string,
  defaultValue: boolean = false,
): [boolean, () => void, () => void, (value: boolean) => void] {
  const [value, setValue] = useLocalStorage(key, defaultValue);

  const toggle = useCallback(() => {
    setValue((prev) => !prev);
  }, [setValue]);

  const setTrue = useCallback(() => {
    setValue(true);
  }, [setValue]);

  const setFalse = useCallback(() => {
    setValue(false);
  }, [setValue]);

  return [value, toggle, setTrue, setFalse];
}
